import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { PriceRangeSlider } from "./PriceRangeSlider.jsx";
import { fetchSearchResults } from "../../../redux/features/searchProductsSlice.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories } from "../../../redux/features/searchCategorySlice.js";
import { useLocation } from "react-router-dom";

function FilterMenuLeft({
  categories = [],
  selectedCategory = [],
  setSelectedCategory = () => { },
  minPrice,
  maxPrice,
  setMinPrice,
  setMaxPrice,
  applyFilters = () => { },
  showFilters,
  onClosePopup = () => { },
  setLoadingFilter = () => { },
}) {
  const { t } = useTranslation();
  const lang = useSelector((state) => state.language.lang);
  const dispatch = useDispatch();
  const location = useLocation();

  // Dropdown visibility
  const [showCategory, setShowCategory] = useState(true);
  const [showPriceRange, setShowPriceRange] = useState(true);

  // Local filter state
  const [tempCategory, setTempCategory] = useState(null);
  const [tempMinPrice, setTempMinPrice] = useState(minPrice);
  const [tempMaxPrice, setTempMaxPrice] = useState(maxPrice);

  useEffect(() => {
    setTempMinPrice(minPrice);
    setTempMaxPrice(maxPrice);
  }, [minPrice, maxPrice]);


  // 🔹 Sync tempCategory with URL whenever location changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const parentCategoryFromUrl = params.get("parent_category_id");
    const newCategory = parentCategoryFromUrl ? String(parentCategoryFromUrl) : null;

    if (newCategory !== tempCategory) {
      setTempCategory(newCategory);
    }
  }, [location.pathname, location.search, showFilters]);

  // Format categories
  const formattedCategories = categories.map((cat) => ({
    ...cat,
    name: cat.title,
  }));

  // 🔹 Handle category select (radio button)
  const handleCategoryChange = async (categoryId) => {
    setLoadingFilter(true);
    const categoryStr = String(categoryId);
    const isSame = tempCategory === categoryStr;
    const newCategory = isSame ? null : categoryStr;
    setTempCategory(newCategory);

    const params = new URLSearchParams(window.location.search);
    params.delete("query");

    if (newCategory) {
      params.set("parent_category_id", newCategory);
      params.delete("category_id");
    } else {
      params.delete("parent_category_id");
      params.delete("category_id");
    }

    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);

    try {
      if (newCategory) {
        await dispatch(fetchCategories({ lang, parent_id: newCategory }));
        await dispatch(
          fetchSearchResults({
            searchQuery: "",
            page: 0,
            limit: 12,
            lang,
            parent_category_id: newCategory,
            min_price: tempMinPrice,
            max_price: tempMaxPrice,
          })
        );
      } else {
        dispatch({ type: "categories/clearChildCategories" });
        await dispatch(
          fetchSearchResults({
            searchQuery: "",
            page: 0,
            limit: 12,
            lang,
            min_price: tempMinPrice,
            max_price: tempMaxPrice,
          })
        );
      }
    } finally {
      // toggleCategoryDropdown(false);
      onClosePopup();
      setLoadingFilter(false);
    }
  };

  // 🔹 Handle child category selection
  const handleChildCategoryChange = (childCategoryId) => {
    const params = new URLSearchParams(window.location.search);
    const parentCategory = params.get("parent_category_id");
    if (!parentCategory) return;

    params.delete("query"); // ✅ Clear search when selecting child
    params.set("category_id", String(childCategoryId));

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params.toString()}`
    );

    dispatch(
      fetchSearchResults({
        searchQuery: "",
        page: 0,
        limit: 12,
        lang,
        parent_category_id: parentCategory,
        category_id: childCategoryId,
        min_price: tempMinPrice,
        max_price: tempMaxPrice,
      })
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get("query")?.trim() || "";

    if (query) {
      // Clear selected category temporarily while a search exists
      setTempCategory(null);
    }
  }, [location.search]);

  // 🔹 Apply filters
  const handleApplyFilters = async ({ categoryId, minPrice, maxPrice }) => {
    setLoadingFilter(true); // 🌀 start loader immediately

    setSelectedCategory(categoryId ? [categoryId] : []);
    setMinPrice(minPrice);
    setMaxPrice(maxPrice);

    const params = new URLSearchParams(window.location.search);

    // Update the URL with price filters
    if (minPrice != null) params.set("min_price", String(minPrice));
    else params.delete("min_price");

    if (maxPrice != null) params.set("max_price", String(maxPrice));
    else params.delete("max_price");

    const parentCategoryFromUrl = params.get("parent_category_id");
    const childCategoryFromUrl = params.get("category_id");

    // Prepare payload for API
    const payload = {
      searchQuery: "",
      page: 0,
      limit: 12,
      lang,
    };

    // Include price from URL if exists
    const urlMinPrice = params.get("min_price");
    const urlMaxPrice = params.get("max_price");
    if (urlMinPrice) payload.min_price = urlMinPrice;
    if (urlMaxPrice) payload.max_price = urlMaxPrice;

    // Keep category context intact
    if (childCategoryFromUrl && childCategoryFromUrl !== '""') {
      payload.category_id = childCategoryFromUrl;
    } else if (parentCategoryFromUrl && parentCategoryFromUrl !== '""') {
      payload.parent_category_id = parentCategoryFromUrl;
    } else if (categoryId) {
      payload.parent_category_id = categoryId;
      params.set("parent_category_id", categoryId);
    }

    // Reflect updates in browser URL
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params.toString()}`
    );

    try {
      // 🔹 Trigger API call
      await dispatch(fetchSearchResults(payload));
    } finally {
      // ✅ Always close and stop loading
      onClosePopup();
      setLoadingFilter(false);
    }
  };

  // 🔹 Reset filters
  const handleResetFilters = () => {
    setTempCategory(null);
    setTempMinPrice(1);
    setTempMaxPrice(5000);
    setSelectedCategory([]);
    setMinPrice(1);
    setMaxPrice(5000);

    const params = new URLSearchParams(window.location.search);
    params.delete("min_price");
    params.delete("max_price");
    params.delete("category_id");
    params.delete("parent_category_id");
    params.delete("query");
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);

    applyFilters({ categoryId: null, minPrice: 1, maxPrice: 5000 });
  };

  return (
    <div className="filter-card p-3 rounded bg-white">
      {/* ===== CATEGORY FILTER ===== */}
      <div className="mb-3">
        <h5
          className="d-flex align-items-center justify-content-between"
          style={{ color: "var(--light-gray)", cursor: "pointer", fontWeight: 500 }}
          onClick={() => setShowCategory(!showCategory)}
        >
          {t("Category")}
          {showCategory ? (
            <KeyboardArrowUp style={{ fontSize: 30 }} />
          ) : (
            <KeyboardArrowDown style={{ fontSize: 30 }} />
          )}
        </h5>

        {showCategory && (
          <>
            {formattedCategories.map((category) => (
              <div key={category.id} className="mb-2">
                {/* Parent Category */}
                <div className="form-check mb-1">
                  <input
                    type="radio"
                    name="parentCategory"
                    className="form-check-input radio-filter-custom"
                    id={`category-${category.id}`}
                    value={category.id}
                    checked={String(tempCategory) === String(category.id)}
                    onClick={(e) => {
                      // If already selected, allow unselect
                      if (String(tempCategory) === String(category.id)) {
                        e.preventDefault(); // stop default radio behavior
                        handleCategoryChange(""); // unselect
                        return;
                      }
                    }}
                    onChange={() => handleCategoryChange(String(category.id))}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`category-${category.id}`}
                    style={{ color: "var(--light-gray)" }}
                  >
                    {category.name}
                  </label>
                </div>

                {/* Child Categories */}
                {category.children && category.children.length > 0 && (
                  <div className="ms-3">
                    {category.children.map((child) => (
                      <div key={child.id} className="form-check mb-1">
                        <input
                          type="radio"
                          name="childCategory"
                          className="form-check-input radio-filter-custom"
                          id={`child-${child.id}`}
                          value={child.id}
                          onClick={() => handleChildCategoryChange(child.id)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`child-${child.id}`}
                          style={{ color: "var(--light-gray)" }}
                        >
                          {child.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* ===== PRICE RANGE FILTER ===== */}
      <div className="mb-3">
        <h5
          className="d-flex align-items-center justify-content-between"
          style={{ color: "var(--light-gray)", cursor: "pointer" }}
          onClick={() => setShowPriceRange(!showPriceRange)}
        >
          {t("Price-Range")}
          {showPriceRange ? (
            <KeyboardArrowUp style={{ fontSize: 30 }} />
          ) : (
            <KeyboardArrowDown style={{ fontSize: 30 }} />
          )}
        </h5>

        {showPriceRange && (
          <PriceRangeSlider
            min={1}
            max={5000}
            tempMinPrice={tempMinPrice}
            tempMaxPrice={tempMaxPrice}
            handlePriceChange={(values) => {
              const [min, max] =
                values[0] <= values[1] ? values : [values[1], values[0]];
              setTempMinPrice(min);
              setTempMaxPrice(max);
            }}
          />
        )}
      </div>

      {/* ===== ACTION BUTTONS ===== */}
      <div className="text-center d-flex flex-column gap-2">
        <button
          className="btn btn-apply-filters"
          onClick={() =>
            handleApplyFilters({
              categoryId: tempCategory,
              minPrice: tempMinPrice,
              maxPrice: tempMaxPrice,
            })
          }
        >
          {t("Apply Filters")}
        </button>
        <button
          className="btn btn-outline-secondary"
          onClick={handleResetFilters}
        >
          {t("Reset Filters")}
        </button>
      </div>
    </div>
  );
}

export default FilterMenuLeft;
